import React, { Component } from "react";
import { Chart } from "react-google-charts";
import i18n from "../i18n";
import moment from "moment";

const DURATION_LIST = [
  { key: "1M", days: 30 },
  { key: "3M", days: 90 },
  { key: "6M", days: 180 },
  { key: "1Y", days: 365 },
  { key: "2Y", days: 730 },
];

const XTICK_CONFIG = {
  "1M":  { points: 5, intervalDays: 8, format: "dd MMM" },
  "3M":  { points: 7, intervalDays: 16, format: "dd MMM" },
  "6M":  { points: 7, intervalDays: 31, format: "MMM YYYY" },
  "1Y":  { points: 5, intervalDays: 92, format: "MMM YYYY" },
  "2Y":  { points: 5, intervalDays: 183, format: "MMM YYYY" },
};

const CHART_LEFT_MARGIN = 45;
const CHART_RIGHT_MARGIN = 30;

class SymptomChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      trend: 2,
      chartState: "",
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.symptomKey !== this.props.symptomKey) {
      // On symptom switch, return to default polynomial state
      if (this.state.trend !== 2) {
        this.setState({ trend: 2 });
      }
    }
  }

  // === Y-Axis Tick Generator - Same as GraphChart ===
  calculateYAxisTicks(dataArray) {
    const multiple = 2;

    if (!Array.isArray(dataArray) || dataArray.length === 0) {
      return [0, 1, 2, 3, 4, 5]; // default fallback
    }

    // Convert to float and filter invalid
    const values = dataArray.map(d => parseFloat(d.y)).filter(v => !isNaN(v));
    let severityMin = Math.min(...values);
    let severityMax = Math.max(...values) + 1.0;

    let start, end;

    if (severityMin === 0) {
      start = 0;
    } else {
      start = Math.floor((severityMin - 1.0) / multiple) * multiple;
    }

    end = Math.ceil(severityMax / multiple) * multiple;

    const interval = (end - start) / 4;

    const ticks = [];
    for (let i = 0; i <= 4; i++) {
      ticks.push(parseFloat((start + interval * i).toFixed(2)));
    }
    ticks.push(parseFloat(end.toFixed(2)));

    return ticks;
  }

  hideFirstXAxisLabel = (chartWrapper) => {
    try {
      const chart = chartWrapper && chartWrapper.getChart && chartWrapper.getChart();
      const container = chart && chart.getContainer && chart.getContainer();
      const svg = container && container.querySelector && container.querySelector("svg");
      if (!svg) return;

      // 1) Unhide anything we may have hidden before
      Array.from(svg.querySelectorAll('text[data-xlabel-hidden="1"]')).forEach((el) => {
        el.style.display = "";
        el.removeAttribute("data-xlabel-hidden");
      });

      // 2) Candidate X labels: bottom row, text-anchor=middle
      const texts = Array.from(svg.querySelectorAll("text"))
        .filter((el) => el.getAttribute("text-anchor") === "middle");
      if (!texts.length) return;

      // Find the bottom row by max Y
      let maxY = -Infinity;
      const withBox = texts
        .map((el) => {
          const box = el.getBBox && el.getBBox();
          if (!box) return null;
          maxY = Math.max(maxY, box.y);
          return { el, box };
        })
        .filter(Boolean);

      const bottom = withBox.filter((x) => maxY - x.box.y < 5);
      if (!bottom.length) return;

      // 3) Prefer text match to avoid hiding the wrong label during initial paint
      const expected = (this.expectedFirstXLabel || "").trim();
      let target = null;
      if (expected) {
        target = (bottom.find((x) => (x.el.textContent || "").trim() === expected) || {}).el || null;
      }
      // Fallback: leftmost if we didn’t find by text
      if (!target) {
        bottom.sort((a, b) => a.box.x - b.box.x);
        target = bottom[0].el;
      }

      // Hide only the intended first label
      target.style.display = "none";
      target.style.pointerEvents = "none";
      target.setAttribute("data-xlabel-hidden", "1");
    } catch (e) {
      console.warn("hideFirstXAxisLabel failed:", e);
    }
  };


  render() {
    const { timeSelection, chartPeriod } = this.props;

    // Count non-null numeric points for each series
    const countValid = (arr) =>
      Array.isArray(arr)
        ? arr.reduce((n, d) => (d && d.y != null && !isNaN(Number(d.y)) ? n + 1 : n), 0)
        : 0;

    // Count number of datapoints
    const dataCount = countValid(this.props.data);

    // ===== Dynamic Tick Calculation for Y-Axis (Left and Right) =====
    const minmax_Symptom = this.calculateYAxisTicks(this.props.data);
    const symptomTicks = minmax_Symptom.length > 0 ? minmax_Symptom : [];

    // ===== Determine Actual Data Range (days) =====
    const timeKey = timeSelection;
    let scaleKey = timeKey;

    const userDuration = DURATION_LIST.find((d) => d.key === timeKey);

    const tickConfig = XTICK_CONFIG[scaleKey];
    const tickCount = tickConfig.points;
    const interval = tickConfig.intervalDays;
    const today = moment().startOf("day");

    // ✅ THEN: Generate xLabelsNew (either mode)
    let xLabels = [];

    // ✅ Count backwards from today
    for (let i = tickCount - 1; i >= 0; i--) {
      const tickDate = today.clone().subtract(i * interval, "days");
      xLabels.push(new Date(Date.UTC(tickDate.year(), tickDate.month(), tickDate.date())));
    }

    const xLabelsFinal = xLabels.map((d, i) => {
      if (i === xLabels.length - 1) {
        return {
          v: d,
          f: i18n.t("general.DATE_PICKER_TEXT") || "Today",
        };
      }
      return d;
    });

    this.expectedFirstXLabel =
      moment(xLabels[0]).format(
        XTICK_CONFIG[scaleKey].format.includes("YYYY") ? "MMM YYYY" : "DD MMM"
      );

    // Trendline to be generated when there's data and the number of datapoints are more than 1
    const trendlines = {};
    if (this.state.trend !== 0 && dataCount >= 2) {
      trendlines[0] = {
        tooltip: false,
        type: "polynomial",
        degree: this.state.trend,
        visibleInLegend: false,
        color: "rgb(255, 0, 0)",
        lineWidth: 3,
        opacity: 1,
      };
    }

    const options = {
      title: null,
      vAxis: {
        type: "number",
        ticks: symptomTicks,
        gridlines: { color: "#454545" },
        minorGridlines: { color: "transparent" },
        viewWindow: {
          min: Math.min(...symptomTicks),
          max: Math.max(...symptomTicks),
        },
        textStyle: {
          // color: "#fff",
          // fontSize: 12,
          color: "#bbb",
          fontSize: 10,
        },
        format: "#.##",
        baseline: Math.min(...symptomTicks) - 1000, // move Google default baseline far beyond graph area
        baselineColor: "transparent" // change the color of Google default baseline to hide it visually
      },
      hAxis: {
        type: "date",
        format:tickConfig.format,
        ticks: xLabelsFinal,
        showTextEvery: 1,
        viewWindow: {
          min: xLabels[0],
          max: xLabels[xLabels.length - 1],
        },
        gridlines: { color: "transparent" },
        minorGridlines: { color: "transparent" },
        textStyle: {
          color: "#bbb",
          fontSize: 10,
          fontStyle: "bold",
        },
        baselineColor: "transparent",
      },      
      legend: "none",
      crosshair: {
        trigger: "none",
        orientation: "both",
        opacity: 0,
      },
      backgroundColor: "transparent",
      chartArea: {
        top: 15,
        right: CHART_RIGHT_MARGIN,
        bottom: 20,
        left: CHART_LEFT_MARGIN,
        backgroundColor: {
          fill: "transparent",
          stroke: "#000",
          opacity: 0,
        },
      },
      enableInteractivity: true,
      series: {
        0: { pointShape: "square", pointSize: 9 },
      },
      pointSize: 7,
      colors: ["red", "blue"],
      trendlines,
      // trendlines: {
      //   0: {
      //     tooltip: false,
      //     type: "polynomial",
      //     degree: this.state.trend,
      //     visibleInLegend: false,
      //     color: "red",
      //     lineWidth: 3,
      //     opacity: this.state.trend === 0 ? 0 : 1,
      //   },
      // }, // Draw a trendline for data series 0.
      tooltip: {
        isHtml: true,
      },
    };

    let data = [
      [
        "Date",
        "Severity",
        { role: "tooltip", type: "string", p: { html: true } },
      ],
    ];
    this.props.data.map((item, index) => {
      data.push([
        new Date(item.x),
        Number(item.y),
        '<div style="color:#fff;background-color:#000;padding:5px;font-size:11px;border:0;border-radius:5px">' +
          moment(new Date(item.x)).format("DDMMMYYYY, h:mma") +
          ", " +
          Number(item.y) +
          "</div>",
      ]);
    });

    return (
      <div>
        {this.props.data.length > 0 && (
          <div
            style={{
              // display: "flex",
              position: "absolute",
              top: 20,
              left: 3,
              right: 0,
              bottom: 22,
            }}
          >
            <div style={{ position: "absolute", top: -15, left: 5 }}>
              {dataCount >= 2 ? (
                // Show the toggle button only when 2+ points
                <span
                  onClick={() => {
                    if (this.state.trend < 4) {
                      this.setState({ trend: this.state.trend + 1 });
                    } else {
                      this.setState({ trend: 0 });
                    }
                  }}
                  style={{
                    backgroundColor: "#ff0000",
                    color: "#fff",
                    fontSize: 12,
                    display: "inline-block",
                    marginRight: 5,
                    padding: "0px 0px 2px 0px",
                    cursor: "pointer",
                    borderRadius: 4,
                    width: "6rem",
                    textAlign: "center",
                  }}
                >
                  {this.state.trend === 0
                    ? i18n.t("general.NO_LINE_BUTTON_TEXT")
                    : this.state.trend === 1
                    ? i18n.t("general.LINEAR_BUTTON_TEXT")
                    : i18n.t("general.POLYNOMINAL_BUTTON_TEXT") +
                      this.state.trend}
                </span>
              ) : dataCount === 1 ? (
                // Placeholder text when exactly 1 datapoint
                <span
                  style={{
                    backgroundColor: "#ff0000",
                    color: "#fff",
                    fontSize: 12,
                    display: "inline-block",
                    marginRight: 5,
                    padding: "0px 0px 2px 0px",
                    borderRadius: 4,
                    width: "6rem",
                    textAlign: "center",
                  }}
                >
                  {i18n.t("graph.ONLY_1_POINT")}
                </span>
              ) : null}
            </div>
            {this.state.chartState === "ready" && (
              <div
                style={{
                  position: "absolute",
                  left: CHART_LEFT_MARGIN,
                  top: 15,
                  right: CHART_RIGHT_MARGIN,
                  bottom: 20,
                }}
              >
                {xLabels &&
                  xLabels.map((item, index) => {
                    // let left = (100 / (xLabels.length - 1)) * index;
                    if (index < xLabels.length) {
                      return (
                        <div
                          key={index}
                          style={{
                            height: "100%",
                            // borderLeft: left === 0 ? "none" : "1px solid lime",
                            borderLeft:
                            index === 0
                              ? "1px solid #454545"
                              : index === xLabels.length - 1
                              ? "1px solid #454545"
                              : "1px solid #454545",
                            position: "absolute",
                            bottom: 0,
                            // left: left + "%",
                            left: (100 / (xLabels.length - 1)) * index + "%",
                            zIndex:
                              index === 0
                                ? 1
                                : index === xLabels.length - 1
                                  ? 1
                                  : undefined,
                          }}
                        />
                      );
                    }
                  })}
              </div>
            )}

            <Chart
              chartType="ScatterChart"
              data={data}
              // loader={<div>Loading Chart</div>}
              options={options}
              chartEvents={[
                {
                  eventName: "ready",
                  callback: ({ chartWrapper }) => {
                    const run = () => {
                      // run twice to catch any late reflow/animation
                      this.hideFirstXAxisLabel(chartWrapper);
                      setTimeout(() => this.hideFirstXAxisLabel(chartWrapper), 0);
                    };

                    if (this.state.chartState !== "ready") {
                      // this.setState({ chartState: "ready" });
                      this.setState({ chartState: "ready" }, () => {
                        // ensure labels are laid out before we pick one to hide
                        requestAnimationFrame(run);
                      });

                    } else {
                      // this.hideFirstXAxisLabel(chartWrapper);
                      requestAnimationFrame(run);
                    }
                  },
                },
                {
                  // Prevent the square outline on any point click (symptom)
                  eventName: "select",
                  callback: ({ chartWrapper }) => {
                    try {
                      const chart = chartWrapper.getChart();
                      if (chart && chart.setSelection) chart.setSelection([]);
                    } catch (_) {
                      /* no-op */
                    }
                  },
                },


              ]}
              width="100%"
              height="100%"
              // legendToggle
              render={({ renderChart }) => {
                return (
                  <div style={{ width: "100%", height: "100%" }}>
                    {renderChart()}
                  </div>
                );
              }}
            />
          </div>
        )}
      </div>
    );
  }
}

export default SymptomChart;



// WEBPACK FOOTER //
// ./src/components/SymptomChart.js